import { apiSlice } from "../../../features/api/apiSlice";

export type ExamDiet = {
  id: string;
  title: string;
  applications_open_at: string;
  applications_close_at: string;
  status: "draft" | "open" | "closed" | "archived";
  is_active: boolean | number;
};

export type ExamLite = {
  id: string;
  code: string;
  title: string;
  exam_type: "type1" | "type2" | "type3" | "type4";
  requires_logbook_stage: "none" | "stage1" | "stage2";
  eligible: boolean;
  ineligible_reason?: string | null;
  centers_count: number;
};

/** A call an application may be deferred into. */
export type DeferTargetCall = {
  id: string;
  title: string | null;
  /**
   * The exam this call belongs to.
   *
   * Always the application's own exam — the server offers no others, and
   * refuses the move again if one is named anyway. It is on the row so the
   * screen can SHOW that, because calls and diets are titled after the cycle
   * rather than the exam and read identically across all of them.
   */
  exam: { id: string; code: string | null; title: string | null } | null;
  diet_id: string;
  diet_title: string | null;
  opens_at: string;
  closes_at: string;
  application_fee: string | number | null;
  is_active: boolean;
  /** Within its window right now — a closed call may still be deferred into. */
  is_open: boolean;
  /** The move would be refused; `blocked_reason` says why. */
  blocked: boolean;
  blocked_reason: string | null;
};

export type DietExamsResponse = {
  diet: ExamDiet;
  user_logbook_stage_passed: number; // 0..3
  exams: ExamLite[];
};

export type Center = {
  id: string;
  name: string;
  code?: string | null;
  address?: string | null;
  state?: string | null;
  capacity?: number | null;
  is_active?: boolean | number;
};

export type CentersResponse = {
  diet: ExamDiet;
  exam: { id: string; title: string; code: string };
  centers: Array<{
    slot_id: string;
    capacity?: number | null;
    center: Center;
  }>;
};

export type ExamApplicationPaper = {
  id: string;
  attempt_no: number;
  status: "pending" | "pass" | "fail" | "absent";
  score?: number | null;
  graded_at?: string | null;
  paper?: {
    id: string;
    code: string;
    title: string;
    pass_mark?: number | null;
  };
};

export type ExamApplication = {
  id: string;
  exam_diet_id: string;
  exam_id: string;
  exam_center_id: string;
  status:
    | "draft"
    | "pending_payment"
    | "paid"
    | "submitted"
    | "under_review"
    | "with_committee"
    | "approved"
    | "rejected"
    | "graded"
    | "completed";
  submitted_at?: string | null;
  diet?: ExamDiet & { session?: { id: number; title: string; year: number; session_number: number } | null };
  exam?: { id: string; title: string; code: string };
  center?: Center;
  papers?: Array<ExamApplicationPaper>;
};

function pick(res: any) {
  return res?.data?.data ?? res?.data ?? res;
}
function pickArr(res: any): any[] {
  const d = pick(res);
  return Array.isArray(d) ? d : [];
}

export const examApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * =========================
     * MEMBER / PROBATIONER
     * =========================
     */

    getOpenExamDiets: builder.query<ExamDiet[], void>({
      query: () => "/exam/exam-diets/open",
      transformResponse: (res: any) => pickArr(res),
      providesTags: (result) =>
        result
          ? [
              ...result.map((d) => ({
                type: "ExamDiets" as const,
                id: d.id,
              })),
              { type: "ExamDiets" as const, id: "OPEN" },
            ]
          : [{ type: "ExamDiets" as const, id: "OPEN" }],
    }),

    getExamsForDiet: builder.query<DietExamsResponse, string>({
      query: (dietId) => `/exam/exam-diets/${dietId}/exams`,
      transformResponse: (res: any) => pick(res) as DietExamsResponse,
      providesTags: (_res, _err, dietId) => [
        { type: "DietExams" as const, id: dietId },
      ],
    }),

    getCentersForExamInDiet: builder.query<
      CentersResponse,
      { dietId: string; examId: string }
    >({
      query: ({ dietId, examId }) =>
        `/exam/exam-diets/${dietId}/exams/${examId}/centers`,
      transformResponse: (res: any) => pick(res) as CentersResponse,
      providesTags: (_res, _err, arg) => [
        { type: "ExamCenters" as const, id: `${arg.dietId}:${arg.examId}` },
      ],
    }),

    createExamApplication: builder.mutation<
      ExamApplication,
      { exam_diet_id: string; exam_id: string; exam_center_id: string }
    >({
      query: (body) => ({
        url: "/exam/exam-applications",
        method: "POST",
        body,
      }),
      invalidatesTags: (_res, _err, body) => [
        { type: "MyExamApps" as const, id: "LIST" },
        { type: "DietExams" as const, id: body.exam_diet_id },
      ],
    }),

    submitExamApplication: builder.mutation<ExamApplication, { id: string }>({
      query: ({ id }) => ({
        url: `/exam/exam-applications/${id}/submit`,
        method: "POST",
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "MyExamApps" as const, id: "LIST" },
        { type: "ExamApp" as const, id: arg.id },
      ],
    }),

    getMyExamApplications: builder.query<ExamApplication[], void>({
      query: () => "/exam/exam-applications/mine",
      transformResponse: (res: any) => pickArr(res),
      providesTags: (result) =>
        result
          ? [
              ...result.map((a) => ({
                type: "MyExamApps" as const,
                id: a.id,
              })),
              { type: "MyExamApps" as const, id: "LIST" },
            ]
          : [{ type: "MyExamApps" as const, id: "LIST" }],
    }),

    getExamApplicationDetails: builder.query<ExamApplication, string>({
      query: (id) => `/exam/exam-applications/${id}`,
      transformResponse: (res: any) => pick(res) as ExamApplication,
      providesTags: (_res, _err, id) => [{ type: "ExamApp" as const, id }],
    }),

    /**
     * =========================
     * ADMIN
     * =========================
     */

    adminGetExams: builder.query<any[], void>({
      query: () => "/exam/admin/exams",
      transformResponse: (res: any) => pickArr(res),
      providesTags: (result) =>
        result
          ? [
              ...result.map((x: any) => ({
                type: "AdminExams" as const,
                id: x.id,
              })),
              { type: "AdminExams" as const, id: "LIST" },
            ]
          : [{ type: "AdminExams" as const, id: "LIST" }],
    }),

    adminCreateExam: builder.mutation<any, any>({
      query: (body) => ({
        url: "/exam/admin/exams",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "AdminExams" as const, id: "LIST" }],
    }),

    adminUpdateExam: builder.mutation<any, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/exam/admin/exams/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "AdminExams" as const, id: "LIST" },
        { type: "AdminExams" as const, id: arg.id },
      ],
    }),

    adminDeleteExam: builder.mutation<any, string>({
      query: (id) => ({
        url: `/exam/admin/exams/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "AdminExams" as const, id: "LIST" }],
    }),

    adminGetExam: builder.query<any, string>({
      query: (id) => `/exam/admin/exams/${id}`,
      transformResponse: (res: any) => pick(res),
      providesTags: (_res, _err, id) => [{ type: "AdminExams" as const, id }],
    }),

    adminGetDiets: builder.query<any[], void>({
      query: () => "/exam/admin/exam-diets",
      transformResponse: (res: any) => pickArr(res),
      providesTags: (result) =>
        result
          ? [
              ...result.map((d: any) => ({
                type: "AdminExamDiets" as const,
                id: d.id,
              })),
              { type: "AdminExamDiets" as const, id: "LIST" },
            ]
          : [{ type: "AdminExamDiets" as const, id: "LIST" }],
    }),

    adminGetDiet: builder.query<any, string>({
      query: (id) => `/exam/admin/exam-diets/${id}`,
      transformResponse: (res: any) => pick(res),
      providesTags: (_res, _err, id) => [{ type: "AdminExamDiets" as const, id }],
    }),

    adminGetCourseGrades: builder.query<any, string>({
      query: (appId) => `/exam/admin/exam-applications/${appId}/course-grades`,
      transformResponse: (res: any) => pick(res),
      providesTags: (_r, _e, appId) => [{ type: "AdminApps" as const, id: `cg:${appId}` }],
    }),

    // Admin-only: compiled result + emergency-override audit for one application.
    adminGetAppResultMeta: builder.query<any, string>({
      query: (appId) => `/exam/admin/exam-applications/${appId}/result-meta`,
      transformResponse: (res: any) => res?.result ?? res?.data?.result ?? null,
      providesTags: (_r, _e, appId) => [{ type: "AdminApps" as const, id: `cg:${appId}` }],
    }),

    // exam_course_id is the course marked — or null for an interview exam,
    // where the interview itself is the single unit of assessment.
    adminSubmitCourseGrade: builder.mutation<
      any,
      { appId: string; body: { exam_course_id: number | null; score: number; remarks?: string } }
    >({
      query: ({ appId, body }) => ({
        url: `/exam/admin/exam-applications/${appId}/course-grades`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: "AdminApps" as const, id: `cg:${arg.appId}` }],
    }),

    adminDeleteCourseGrade: builder.mutation<any, { gradeId: string; appId: string }>({
      query: ({ gradeId }) => ({
        url: `/exam/admin/exam-course-grades/${gradeId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: "AdminApps" as const, id: `cg:${arg.appId}` }],
    }),

    adminCompileResult: builder.mutation<
      any,
      { appId: string; body: { overall_status?: string; pass_mark?: number; remarks?: string; force?: boolean } }
    >({
      query: ({ appId, body }) => ({
        url: `/exam/admin/exam-applications/${appId}/compile-result`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "AdminApps" as const, id: `cg:${arg.appId}` },
        { type: "AdminApps" as const, id: "LIST" },
      ],
    }),

    adminReleaseResult: builder.mutation<any, { appId: string }>({
      query: ({ appId }) => ({
        url: `/exam/admin/exam-applications/${appId}/release-result`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "AdminApps" as const, id: `cg:${arg.appId}` },
        { type: "AdminApps" as const, id: "LIST" },
      ],
    }),

    // EMERGENCY: Chief Examiner sets the final result directly, bypassing the
    // ≥3-examiner average protocol. Reason is mandatory; fully audited.
    adminOverrideResult: builder.mutation<
      any,
      {
        appId: string;
        body: {
          reason: string;
          // One entry per registered course; a single entry with a null course
          // id for an interview exam.
          scores: { exam_course_id: number | null; score: number }[];
          pass_mark?: number;
          publish?: boolean;
        };
      }
    >({
      query: ({ appId, body }) => ({
        url: `/exam/admin/exam-applications/${appId}/override-result`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "AdminApps" as const, id: `cg:${arg.appId}` },
        { type: "AdminApps" as const, id: "LIST" },
      ],
    }),

    // Bulk compile / release for a whole diet.
    adminCompileDietResults: builder.mutation<any, { dietId: string; body?: { pass_mark?: number; force?: boolean } }>({
      query: ({ dietId, body }) => ({
        url: `/exam/admin/exam-diets/${dietId}/compile-results`,
        method: "POST",
        body: body ?? {},
      }),
      invalidatesTags: [{ type: "AdminApps" as const, id: "LIST" }],
    }),

    // Every mark the examiners actually recorded in a diet, by course and
    // examiner. Replaces the retired exam_result_batches view, which the
    // course-average pipeline never writes to.
    adminGetExaminerMarks: builder.query<any[], { dietId: string }>({
      query: ({ dietId }) => `/exam/admin/exam-diets/${dietId}/examiner-marks`,
      transformResponse: (res: any) => pickArr(res),
      providesTags: (_r, _e, arg) => [{ type: "AdminApps" as const, id: `marks:${arg.dietId}` }],
    }),

    // Where a diet stands between the board's publication and the
    // secretariat's release.
    adminReleasePreview: builder.query<
      {
        candidates: number;
        published_by_board: number;
        awaiting_board: number;
        /**
         * Closed by one of the older grading screens rather than by the Chief
         * Examiner — those end an application `completed` without a board pass
         * behind it. Zero on anything graded through the board; on an old
         * sitting it is why the diet counts as ready without the board having
         * published it.
         */
        closed_by_grading?: number;
        already_released: number;
        to_release: number;
        ready: boolean;
        results_released_at: string | null;
      },
      { dietId: string }
    >({
      query: ({ dietId }) => `/exam/admin/exam-diets/${dietId}/release-preview`,
      providesTags: (_r, _e, arg) => [{ type: "AdminApps" as const, id: `rel:${arg.dietId}` }],
    }),

    // What publishing would do, before it is done — publication is final.
    adminPublishPreview: builder.query<
      {
        candidates: number;
        already_published: number;
        to_publish: number;
        to_mark_absent: number;
        to_compile: number;
        incomplete_marking: number;
        // Marked but never compiled — these block publication.
        not_compiled: number;
        absentees: { application_id: string; exam_no: string | null; name: string | null }[];
        uncompiled: { application_id: string; exam_no: string | null; name: string | null }[];
      },
      { dietId: string }
    >({
      query: ({ dietId }) => `/exam/admin/exam-diets/${dietId}/publish-preview`,
      providesTags: (_r, _e, arg) => [{ type: "AdminApps" as const, id: `pub:${arg.dietId}` }],
    }),

    // The exact reverse of the Chief Examiner's PUBLISH: reopens the diet for
    // grading. Refused once the secretariat has released any of it.
    adminUnpublishDietResults: builder.mutation<any, { dietId: string }>({
      query: ({ dietId }) => ({
        url: `/exam/admin/exam-diets/${dietId}/unpublish-results`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "AdminApps" as const, id: "LIST" },
        { type: "AdminApps" as const, id: `diet:${arg.dietId}` },
        { type: "AdminApps" as const, id: `pub:${arg.dietId}` },
        { type: "AdminApps" as const, id: `rel:${arg.dietId}` },
      ],
    }),

    // The Chief Examiner's PUBLISH: compiles what has marks, records the
    // unmarked absent, and closes the diet. Published results cannot be edited.
    adminReleaseDietResults: builder.mutation<any, { dietId: string; body?: { pass_mark?: number } }>({
      query: ({ dietId, body }) => ({
        url: `/exam/admin/exam-diets/${dietId}/release-results`,
        method: "POST",
        body: body ?? {},
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "AdminApps" as const, id: "LIST" },
        { type: "AdminApps" as const, id: `diet:${arg.dietId}` },
        { type: "AdminApps" as const, id: `pub:${arg.dietId}` },
      ],
    }),

    // ── The candidate's application form (TPC/PCI), staff view ─────────────
    adminGetApplicationForm: builder.query<any, string>({
      query: (appId) => `/exam/admin/exam-applications/${appId}/form`,
      transformResponse: (res: any) => res ?? {},
      providesTags: (_r, _e, appId) => [{ type: "AdminApps" as const, id: `form:${appId}` }],
    }),

    // Review decisions on an application that was sent in for review.
    adminReviewApplication: builder.mutation<
      any,
      { appId: string; action: "approve" | "reject" | "return"; reason?: string }
    >({
      query: ({ appId, action, reason }) => ({
        url: `/exam/admin/exam-applications/${appId}/${action}`,
        method: "POST",
        body: reason ? { reason } : {},
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "AdminApps" as const, id: `form:${arg.appId}` },
        { type: "AdminApps" as const, id: "LIST" },
      ],
    }),

    /* ── The review pipeline: chapter → secretariat ─────────────────────────
     *
     * A submitted application waits on its candidate's own chapter. The
     * endorsement sends it to the secretariat (`under_review`), who approve it,
     * reject it, or hand it back to the candidate with a note — all three
     * through adminReviewApplication above.
     *
     * The Membership Committee rung between the secretariat and the decision
     * was removed on 2026-09-13, and its five endpoints with it. The server
     * still answers them (reads emptily, writes 410) so an older portal build
     * says something a person can act on, but nothing here calls them.
     */

    // Chapter leadership: paid exam applications from their own chapter.
    chapterExamApplications: builder.query<any, { status?: string; diet_id?: string } | void>({
      query: (arg) => {
        const params = new URLSearchParams();
        if (arg?.status && arg.status !== "all") params.set("status", arg.status);
        if (arg?.diet_id) params.set("diet_id", arg.diet_id);
        const qs = params.toString();
        return `/chapter/my-exam-applications${qs ? `?${qs}` : ""}`;
      },
      transformResponse: (res: any) => res ?? { data: [] },
      providesTags: [{ type: "AdminApps" as const, id: "CHAPTER_EXAMS" }],
    }),
    chapterExamApplication: builder.query<any, string>({
      query: (id) => `/chapter/exam-applications/${id}`,
      transformResponse: (res: any) => pick(res),
      providesTags: (_r, _e, id) => [{ type: "AdminApps" as const, id: `chapter-exam:${id}` }],
    }),
    chapterEndorseExamApplication: builder.mutation<any, { id: string; comment: string }>({
      query: ({ id, comment }) => ({
        url: `/chapter/exam-applications/${id}/accept`,
        method: "POST",
        body: { comment },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "AdminApps" as const, id: "CHAPTER_EXAMS" },
        { type: "AdminApps" as const, id: `chapter-exam:${arg.id}` },
      ],
    }),
    chapterDeclineExamApplication: builder.mutation<any, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/chapter/exam-applications/${id}/reject`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "AdminApps" as const, id: "CHAPTER_EXAMS" },
        { type: "AdminApps" as const, id: `chapter-exam:${arg.id}` },
      ],
    }),

    // Section 11 — chapter chairman/secretary endorsement, recorded by staff
    // from the signed paper form.
    adminEndorseApplicationForm: builder.mutation<any, { appId: string; body: any }>({
      query: ({ appId, body }) => ({
        url: `/exam/admin/exam-applications/${appId}/endorsement`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: "AdminApps" as const, id: `form:${arg.appId}` }],
    }),

    // ── Chief Examiner: assign examiners to courses (min 3 per course) ──────
    adminGetCourseExaminers: builder.query<any, string>({
      query: (dietId) => `/exam/admin/exam-diets/${dietId}/course-examiners`,
      transformResponse: (res: any) => pick(res),
      providesTags: (_r, _e, dietId) => [{ type: "AdminApps" as const, id: `ce:${dietId}` }],
    }),

    // courseId is a course id, or the literal "panel" for an interview diet's
    // examining panel (an interview has no courses).
    adminSyncCourseExaminers: builder.mutation<
      any,
      { dietId: string; courseId: number | string; examiner_user_ids: number[] }
    >({
      query: ({ dietId, courseId, examiner_user_ids }) => ({
        url: `/exam/admin/exam-diets/${dietId}/courses/${courseId}/examiners`,
        method: "PUT",
        body: { examiner_user_ids },
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: "AdminApps" as const, id: `ce:${arg.dietId}` }],
    }),

    adminDeleteCourseExaminer: builder.mutation<any, { id: string; dietId: string }>({
      query: ({ id }) => ({ url: `/exam/admin/course-examiners/${id}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, arg) => [{ type: "AdminApps" as const, id: `ce:${arg.dietId}` }],
    }),

    // Chief Examiner: candidate × examiner marks grid for one course.
    adminGetCourseGradesMatrix: builder.query<any, { dietId: string; courseId: number | string }>({
      query: ({ dietId, courseId }) => `/exam/admin/exam-diets/${dietId}/courses/${courseId}/grades-matrix`,
      transformResponse: (res: any) =>
        res?.data && !Array.isArray(res.data) && res.data.candidates !== undefined ? res.data : res,
      providesTags: (_r, _e, arg) => [{ type: "AdminApps" as const, id: `matrix:${arg.dietId}:${arg.courseId}` }],
    }),

    // ── Examiner: the courses the Chief Examiner assigned me to mark ────────
    examinerMyCourseAssignments: builder.query<any[], void>({
      query: () => `/exam/examiner/my-course-assignments`,
      transformResponse: (res: any) => {
        const d = pick(res);
        return Array.isArray(d) ? d : [];
      },
      providesTags: [{ type: "ExaminerAssignments" as const, id: "MARKING" }],
    }),

    examinerGetCourseCandidates: builder.query<any, { dietId: string; courseId: string | number }>({
      query: ({ dietId, courseId }) => `/exam/examiner/diets/${dietId}/courses/${courseId}/candidates`,
      // Keep the full wrapper ({ data: [...], course_name, course_code }); the
      // page reads `.data` (candidates) plus the course meta. Only unwrap an
      // extra Laravel `{ data: { ... } }` envelope if one is present.
      transformResponse: (res: any) =>
        res?.data && !Array.isArray(res.data) && (res.data.data !== undefined || res.data.course_id !== undefined)
          ? res.data
          : res,
      providesTags: (_r, _e, arg) => [{ type: "ExaminerAssignments" as const, id: `cand:${arg.dietId}:${arg.courseId}` }],
    }),

    adminGradeDietApplication: builder.mutation<
      any,
      { dietId: string; appId: string; body: any }
    >({
      query: ({ dietId, appId, body }) => ({
        url: `/exam/admin/exam-diets/${dietId}/applications/${appId}/grade`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "AdminApps" as const, id: `diet:${arg.dietId}` },
        { type: "AdminApps" as const, id: "LIST" },
      ],
    }),

    // Publish a single graded result.
    adminPublishResult: builder.mutation<any, { dietId: string; appId: string }>({
      query: ({ dietId, appId }) => ({
        url: `/exam/admin/exam-diets/${dietId}/applications/${appId}/publish-result`,
        method: "POST",
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "AdminApps" as const, id: `diet:${arg.dietId}` },
      ],
    }),

    // Publish every graded-but-unpublished result in the diet.
    adminPublishResults: builder.mutation<any, { dietId: string }>({
      query: ({ dietId }) => ({
        url: `/exam/admin/exam-diets/${dietId}/publish-results`,
        method: "POST",
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "AdminApps" as const, id: `diet:${arg.dietId}` },
        { type: "AdminApps" as const, id: `rel:${arg.dietId}` },
      ],
    }),

    // ── Backfilling a closed diet (super admin) ──
    // Members with a portal account who are NOT already in this diet — the
    // pool a hand-entered result can be attached to. The server refuses under
    // two characters and returns at most fifteen, so this is safe to fire on
    // every keystroke behind a debounce.
    adminManualCandidates: builder.query<
      {
        user_id: number;
        member_id: string;
        name: string;
        membership_no: string | null;
        email: string | null;
        grade: string | null;
      }[],
      { dietId: string; q: string }
    >({
      query: ({ dietId, q }) =>
        `/exam/admin/exam-diets/${dietId}/manual-candidates?q=${encodeURIComponent(q)}`,
      transformResponse: (res: any) => pickArr(res),
    }),

    // Creates the registration AND its result in one act, marked as entered by
    // hand. Invalidates the diet's applications and its release preview: the
    // new candidate counts towards both.
    adminAddManualResult: builder.mutation<any, { dietId: string; body: any }>({
      query: ({ dietId, body }) => ({
        url: `/exam/admin/exam-diets/${dietId}/manual-result`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "AdminApps" as const, id: `diet:${arg.dietId}` },
        { type: "AdminApps" as const, id: `rel:${arg.dietId}` },
        { type: "AdminApps" as const, id: "LIST" },
      ],
    }),

    // Who can be carried INTO this diet — members with an account who are not
    // already in it.
    adminGetDeferredCandidates: builder.query<
      {
        user_id: number;
        member_id: string;
        name: string;
        membership_no: string | null;
        email: string | null;
        grade: string | null;
      }[],
      { dietId: string; q: string }
    >({
      query: ({ dietId, q }) =>
        `/exam/admin/exam-diets/${dietId}/deferred-candidates?q=${encodeURIComponent(q)}`,
      transformResponse: (res: any) => pickArr(res),
    }),

    // Creates a registration marked as carried over from an earlier sitting.
    // Invalidates the diet's applications: the new candidate counts towards it.
    adminAddDeferredApplication: builder.mutation<any, { dietId: string; body: any }>({
      query: ({ dietId, body }) => ({
        url: `/exam/admin/exam-diets/${dietId}/deferred-application`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "AdminApps" as const, id: `diet:${arg.dietId}` },
        { type: "AdminApps" as const, id: "LIST" },
      ],
    }),

    // Per-diet result-letter settings (signatory, signature image, date, ref prefix).
    adminUpdateDietResultSettings: builder.mutation<any, { dietId: string; body: FormData }>({
      query: ({ dietId, body }) => ({
        url: `/exam/admin/exam-diets/${dietId}/result-settings`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "AdminExamDiets" as const, id: arg.dietId },
      ],
    }),

    adminGetDietApplications: builder.query<
      any[],
      { dietId: string; status?: string }
    >({
      query: ({ dietId, status }) => {
        const q = new URLSearchParams();
        if (status) q.set("status", status);
        const qs = q.toString();
        return `/exam/admin/exam-diets/${dietId}/applications${qs ? `?${qs}` : ""}`;
      },
      transformResponse: (res: any) => pickArr(res),
      providesTags: (_res, _err, arg) => [
        { type: "AdminApps" as const, id: `diet:${arg.dietId}` },
      ],
    }),

    /**
     * The diet's candidates with their declared qualifications and posts held —
     * the numbered listings the exams office prints.
     *
     * `scope` picks which listing: the approved candidates (the default — who
     * is sitting), or everyone who has settled the fee, which carries each
     * payment's reference, amount and date. The server knows only those two
     * scopes and answers with the one it produced; it is not a free filter.
     *
     * Separate from the query above, and fetched on demand (see the lazy hook),
     * because those two are hasMany relations: a candidate with four posts and
     * three degrees is seven extra rows, and nobody opening the diet screen
     * wants them.
     */
    adminGetDietApplicationsExport: builder.query<
      any,
      { dietId: string; scope?: "approved" | "paid" }
    >({
      query: ({ dietId, scope }) =>
        `/exam/admin/exam-diets/${dietId}/applications/export${
          scope === "paid" ? "?scope=paid" : ""
        }`,
    }),

    /**
     * What this diet's candidates registered to sit — per subject and per
     * candidate.
     *
     * Its own endpoint and fetched on demand, like the export above: the
     * registrations are a hasMany, and the listing behind the diet screen is
     * read on every visit by people who want none of them.
     */
    adminGetDietCoursesReport: builder.query<any, { dietId: string }>({
      query: ({ dietId }) => `/exam/admin/exam-diets/${dietId}/courses-report`,
    }),

    // Admin read-only view of examiner-submitted result batches (what the Chief
    // Examiner reviews) + per-batch scores.
    adminGetResultBatches: builder.query<any[], { dietId?: string; status?: string } | void>({
      query: (arg) => {
        const q = new URLSearchParams();
        if (arg && (arg as any).dietId) q.set("diet_id", (arg as any).dietId);
        if (arg && (arg as any).status) q.set("status", (arg as any).status);
        const qs = q.toString();
        return `/exam/admin/result-batches${qs ? `?${qs}` : ""}`;
      },
      transformResponse: (res: any) => pickArr(res),
    }),

    adminGetBatchScores: builder.query<any[], string | number>({
      query: (id) => `/exam/admin/result-batches/${id}/scores`,
      transformResponse: (res: any) => pickArr(res),
    }),

    adminGetDietsByExam: builder.query<any[], string>({
      query: (examId) => `/exam/admin/exam-diets?exam_id=${examId}`,
      transformResponse: (res: any) => pickArr(res),
      providesTags: (result, _err, examId) =>
        result
          ? [
              ...result.map((d: any) => ({
                type: "AdminExamDiets" as const,
                id: d.id,
              })),
              { type: "AdminExamDiets" as const, id: `exam:${examId}` },
            ]
          : [{ type: "AdminExamDiets" as const, id: `exam:${examId}` }],
    }),

    adminCreateDiet: builder.mutation<any, any>({
      query: (body) => ({
        url: "/exam/admin/exam-diets",
        method: "POST",
        body,
      }),
      invalidatesTags: (_res, _err, body) => [
        { type: "AdminExamDiets" as const, id: "LIST" },
        { type: "AdminExamDiets" as const, id: `exam:${body?.exam_id}` },
        { type: "ExamDiets" as const, id: "OPEN" },
        // Refresh session detail if diet is being linked at creation time
        ...(body?.exam_session_id
          ? [{ type: "ExamDiets" as const, id: `session:${body.exam_session_id}` }]
          : []),
      ],
    }),

    adminUpdateDiet: builder.mutation<any, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/exam/admin/exam-diets/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "AdminExamDiets" as const, id: "LIST" },
        { type: "AdminExamDiets" as const, id: arg.id },
        { type: "ExamDiets" as const, id: "OPEN" },
        ...(arg.body?.exam_session_id
          ? [{ type: "ExamDiets" as const, id: `session:${arg.body.exam_session_id}` }]
          : []),
      ],
    }),

    adminDeleteDiet: builder.mutation<any, string>({
      query: (id) => ({
        url: `/exam/admin/exam-diets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "AdminExamDiets" as const, id: "LIST" },
        { type: "ExamDiets" as const, id: "OPEN" },
      ],
    }),

    adminGetCenters: builder.query<any[], void>({
      query: () => "/exam/admin/exam-centers",
      transformResponse: (res: any) => pickArr(res),
      providesTags: (result) =>
        result
          ? [
              ...result.map((c: any) => ({
                type: "AdminExamCenters" as const,
                id: c.id,
              })),
              { type: "AdminExamCenters" as const, id: "LIST" },
            ]
          : [{ type: "AdminExamCenters" as const, id: "LIST" }],
    }),

    adminCreateCenter: builder.mutation<any, any>({
      query: (body) => ({
        url: "/exam/admin/exam-centers",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "AdminExamCenters" as const, id: "LIST" }],
    }),

    adminUpdateCenter: builder.mutation<any, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/exam/admin/exam-centers/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "AdminExamCenters" as const, id: "LIST" },
        { type: "AdminExamCenters" as const, id: arg.id },
      ],
    }),

    adminDeleteCenter: builder.mutation<any, string>({
      query: (id) => ({
        url: `/exam/admin/exam-centers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "AdminExamCenters" as const, id: "LIST" }],
    }),

    adminGetSlots: builder.query<any[], string>({
      query: (dietId) => `/exam/admin/exam-diets/${dietId}/center-slots`,
      transformResponse: (res: any) => pickArr(res),
      providesTags: (_res, _err, dietId) => [
        { type: "AdminSlots" as const, id: dietId },
      ],
    }),

    adminUpsertSlot: builder.mutation<
      any,
      { dietId: string; examId: string; centerId: string; body?: any }
    >({
      query: ({ dietId, examId, centerId, body }) => ({
        url: `/exam/admin/exam-diets/${dietId}/exams/${examId}/centers/${centerId}`,
        method: "PUT",
        body: body || {},
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "AdminSlots" as const, id: arg.dietId },
        { type: "ExamCenters" as const, id: `${arg.dietId}:${arg.examId}` },
      ],
    }),

    adminDeleteSlot: builder.mutation<
      any,
      { dietId: string; examId: string; centerId: string }
    >({
      query: ({ dietId, examId, centerId }) => ({
        url: `/exam/admin/exam-diets/${dietId}/exams/${examId}/centers/${centerId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "AdminSlots" as const, id: arg.dietId },
        { type: "ExamCenters" as const, id: `${arg.dietId}:${arg.examId}` },
      ],
    }),

    adminListApplications: builder.query<
      any[],
      { status?: string; diet_id?: string; exam_id?: string } | void
    >({
      query: (params) => {
        const q = new URLSearchParams();
        if ((params as any)?.status) q.set("status", (params as any).status);
        if ((params as any)?.diet_id) q.set("diet_id", (params as any).diet_id);
        if ((params as any)?.exam_id) q.set("exam_id", (params as any).exam_id);
        const s = q.toString();
        return `/exam/admin/exam-applications${s ? `?${s}` : ""}`;
      },
      transformResponse: (res: any) => pickArr(res),
      providesTags: (result) =>
        result
          ? [
              ...result.map((a: any) => ({
                type: "AdminApps" as const,
                id: a.id,
              })),
              { type: "AdminApps" as const, id: "LIST" },
            ]
          : [{ type: "AdminApps" as const, id: "LIST" }],
    }),

    adminGradeApplication: builder.mutation<
      any,
      { applicationId: string; body: any }
    >({
      query: ({ applicationId, body }) => ({
        url: `/exam/admin/exam-applications/${applicationId}/grade`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "AdminApps" as const, id: "LIST" },
        { type: "AdminApps" as const, id: arg.applicationId },
      ],
    }),

    adminSetExamNo: builder.mutation<
      any,
      { applicationId: string; exam_no: string | null }
    >({
      query: ({ applicationId, exam_no }) => ({
        url: `/exam/admin/exam-applications/${applicationId}/exam-no`,
        method: "POST",
        body: { exam_no },
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "AdminApps" as const, id: "LIST" },
        { type: "AdminApps" as const, id: arg.applicationId },
      ],
    }),

    /**
     * Move an application to another call (or, the older way, another diet).
     * Admin-only: a candidate never defers their own application.
     *
     * One of `target_call_id` / `target_diet_id` is required — the call is
     * preferred, since that is what the candidate actually applied to.
     */
    adminDeferApplication: builder.mutation<
      any,
      {
        applicationId: string;
        target_call_id?: string;
        target_diet_id?: string;
        note?: string;
      }
    >({
      query: ({ applicationId, ...body }) => ({
        url: `/exam/admin/exam-applications/${applicationId}/defer`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "AdminApps" as const, id: "LIST" },
        { type: "AdminApps" as const, id: arg.applicationId },
      ],
    }),

    /** The calls this application can be deferred into, open/blocked flags included. */
    adminGetApplicationDeferTargets: builder.query<
      { application: any; data: DeferTargetCall[] },
      string
    >({
      query: (applicationId) =>
        `/exam/admin/exam-applications/${applicationId}/defer-targets`,
      providesTags: (_r, _e, id) => [{ type: "AdminApps" as const, id }],
    }),

    adminChangeApplicationCenter: builder.mutation<
      any,
      { applicationId: string; exam_center_id: string }
    >({
      query: ({ applicationId, exam_center_id }) => ({
        url: `/exam/admin/exam-applications/${applicationId}/change-center`,
        method: "POST",
        body: { exam_center_id },
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "AdminApps" as const, id: "LIST" },
        { type: "AdminApps" as const, id: arg.applicationId },
      ],
    }),

    adminGetApplicationDeferrals: builder.query<any[], string>({
      query: (applicationId) => `/exam/admin/exam-applications/${applicationId}/deferrals`,
      providesTags: (_r, _e, id) => [{ type: "AdminApps" as const, id }],
    }),

    adminGetExamAnalytics: builder.query<
      any,
      { examId: string; dietId?: string }
    >({
      query: ({ examId, dietId }) => ({
        url: `/exam/admin/exams/${examId}/analytics${
          dietId ? `?diet_id=${dietId}` : ""
        }`,
        method: "GET",
      }),
      providesTags: (_r, _e, a) => [{ type: "AdminApps", id: a.examId }],
    }),

    /* ============================================================
     * EXAM SESSIONS (shared sitting period across TPC / GDE / Intermediate)
     * ============================================================ */

    adminGetExamSessions: builder.query<any, void>({
      query: () => "/exam/admin/exam-sessions",
      providesTags: [{ type: "ExamDiets" as const, id: "SESSIONS" }],
    }),

    adminGetExamSession: builder.query<any, number>({
      query: (id) => `/exam/admin/exam-sessions/${id}`,
      providesTags: (_r, _e, id) => [{ type: "ExamDiets" as const, id: `session:${id}` }],
    }),

    adminCreateExamSession: builder.mutation<any, { title: string; year: number; session_number: number }>({
      query: (body) => ({ url: "/exam/admin/exam-sessions", method: "POST", body }),
      invalidatesTags: [{ type: "ExamDiets" as const, id: "SESSIONS" }],
    }),

    adminUpdateExamSession: builder.mutation<any, { id: number; body: any }>({
      query: ({ id, body }) => ({ url: `/exam/admin/exam-sessions/${id}`, method: "PUT", body }),
      invalidatesTags: (_r, _e, a) => [
        { type: "ExamDiets" as const, id: "SESSIONS" },
        { type: "ExamDiets" as const, id: `session:${a.id}` },
      ],
    }),

    adminDeleteExamSession: builder.mutation<any, number>({
      query: (id) => ({ url: `/exam/admin/exam-sessions/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "ExamDiets" as const, id: "SESSIONS" }],
    }),

    adminAttachDietToSession: builder.mutation<any, { sessionId: number; diet_id: string }>({
      query: ({ sessionId, diet_id }) => ({
        url: `/exam/admin/exam-sessions/${sessionId}/attach-diet`,
        method: "POST",
        body: { diet_id },
      }),
      invalidatesTags: (_r, _e, a) => [{ type: "ExamDiets" as const, id: `session:${a.sessionId}` }],
    }),

    adminDetachDietFromSession: builder.mutation<any, { sessionId: number; diet_id: string }>({
      query: ({ sessionId, diet_id }) => ({
        url: `/exam/admin/exam-sessions/${sessionId}/detach-diet`,
        method: "POST",
        body: { diet_id },
      }),
      invalidatesTags: (_r, _e, a) => [{ type: "ExamDiets" as const, id: `session:${a.sessionId}` }],
    }),

    /* ============================================================
     * TIMETABLE ENTRIES (per session, exam selected from dropdown)
     * ============================================================ */

    getSessionTimetable: builder.query<any, number>({
      query: (sessionId) => `/exam/exam-sessions/${sessionId}/timetable`,
      providesTags: (_r, _e, id) => [{ type: "ExamDiets" as const, id: `timetable:${id}` }],
    }),

    adminGetSessionTimetable: builder.query<any, number>({
      query: (sessionId) => `/exam/admin/exam-sessions/${sessionId}/timetable`,
      providesTags: (_r, _e, id) => [{ type: "ExamDiets" as const, id: `timetable:${id}` }],
    }),

    adminCreateTimetableEntry: builder.mutation<any, { sessionId: number; body: any }>({
      query: ({ sessionId, body }) => ({
        url: `/exam/admin/exam-sessions/${sessionId}/timetable`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, a) => [{ type: "ExamDiets" as const, id: `timetable:${a.sessionId}` }],
    }),

    adminUpdateTimetableEntry: builder.mutation<any, { sessionId: number; id: number; body: any }>({
      query: ({ sessionId, id, body }) => ({
        url: `/exam/admin/exam-sessions/${sessionId}/timetable/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, a) => [{ type: "ExamDiets" as const, id: `timetable:${a.sessionId}` }],
    }),

    adminDeleteTimetableEntry: builder.mutation<any, { sessionId: number; id: number }>({
      query: ({ sessionId, id }) => ({
        url: `/exam/admin/exam-sessions/${sessionId}/timetable/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, a) => [{ type: "ExamDiets" as const, id: `timetable:${a.sessionId}` }],
    }),

    /* ============================================================
     * EXAM COURSES (per exam, used in timetable entry form)
     * ============================================================ */

    adminGetCoursesByExam: builder.query<any[], string>({
      query: (examId) => `/exam/admin/courses?exam_id=${examId}`,
      transformResponse: (res: any) => {
        const d = res?.data ?? res;
        return Array.isArray(d) ? d : [];
      },
      providesTags: (_r, _e, examId) => [{ type: "AdminExamCourses" as const, id: examId }],
    }),

    /* ============================================================
     * ADMISSION CARDS
     * ============================================================ */

    getMyAdmissionCard: builder.query<any, string>({
      query: (applicationId) => `/exam/exam-applications/${applicationId}/admission-card`,
    }),

    adminGetAdmissionCard: builder.query<any, string>({
      query: (applicationId) => `/exam/admin/exam-applications/${applicationId}/admission-card`,
    }),

    adminGetSessionAdmissionCards: builder.query<any, number>({
      query: (sessionId) => `/exam/admin/exam-sessions/${sessionId}/admission-cards`,
    }),

    /* ============================================================
     * APPLICATION COURSE REGISTRATION
     * ============================================================ */

    adminGetApplicationCourses: builder.query<any, string>({
      query: (appId) => `/exam/admin/exam-applications/${appId}/registered-courses`,
      transformResponse: (res: any) => pick(res),
      providesTags: (_r, _e, appId) => [
        { type: "AdminApps" as const, id: `courses:${appId}` },
      ],
    }),

    adminRegisterApplicationCourse: builder.mutation<
      any,
      { appId: string; course_id: number }
    >({
      query: ({ appId, course_id }) => ({
        url: `/exam/admin/exam-applications/${appId}/registered-courses`,
        method: "POST",
        body: { course_id },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "AdminApps" as const, id: `courses:${arg.appId}` },
      ],
    }),

    adminUnregisterApplicationCourse: builder.mutation<
      any,
      { appId: string; courseId: number }
    >({
      query: ({ appId, courseId }) => ({
        url: `/exam/admin/exam-applications/${appId}/registered-courses/${courseId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "AdminApps" as const, id: `courses:${arg.appId}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  // member
  useGetOpenExamDietsQuery,
  useGetExamsForDietQuery,
  useGetCentersForExamInDietQuery,
  useCreateExamApplicationMutation,
  useSubmitExamApplicationMutation,
  useGetMyExamApplicationsQuery,
  useGetExamApplicationDetailsQuery,

  // admin
  useAdminGetExamsQuery,
  useAdminGetExamAnalyticsQuery,
  useAdminCreateExamMutation,
  useAdminUpdateExamMutation,
  useAdminDeleteExamMutation,
  useAdminGetExamQuery,
  useAdminGetDietsQuery,
  useAdminGetDietQuery,
  useAdminGetCourseGradesQuery,
  useAdminSubmitCourseGradeMutation,
  useAdminDeleteCourseGradeMutation,
  useAdminCompileResultMutation,
  useAdminReleaseResultMutation,
  useAdminOverrideResultMutation,
  useAdminGetAppResultMetaQuery,
  useAdminCompileDietResultsMutation,
  useAdminGetExaminerMarksQuery,
  useAdminReleasePreviewQuery,
  useAdminPublishPreviewQuery,
  useAdminReleaseDietResultsMutation,
  useAdminUnpublishDietResultsMutation,
  useAdminGetCourseExaminersQuery,
  useAdminSyncCourseExaminersMutation,
  useAdminDeleteCourseExaminerMutation,
  useAdminGetCourseGradesMatrixQuery,
  useAdminGetApplicationFormQuery,
  useAdminReviewApplicationMutation,
  useAdminEndorseApplicationFormMutation,

  // review pipeline: chapter → secretariat
  useChapterExamApplicationsQuery,
  useChapterExamApplicationQuery,
  useChapterEndorseExamApplicationMutation,
  useChapterDeclineExamApplicationMutation,
  useExaminerMyCourseAssignmentsQuery,
  useExaminerGetCourseCandidatesQuery,
  useAdminGradeDietApplicationMutation,
  useAdminGetDietApplicationsQuery,
  // Lazy: the export is fetched when the button is pressed, not on page load.
  useLazyAdminGetDietApplicationsExportQuery,
  useLazyAdminGetDietCoursesReportQuery,
  useAdminGetResultBatchesQuery,
  useAdminGetBatchScoresQuery,
  useAdminPublishResultMutation,
  useAdminPublishResultsMutation,
  useAdminUpdateDietResultSettingsMutation,
  useAdminManualCandidatesQuery,
  useAdminAddManualResultMutation,
  useAdminGetDeferredCandidatesQuery,
  useAdminAddDeferredApplicationMutation,
  useAdminGetDietsByExamQuery,
  useAdminCreateDietMutation,
  useAdminUpdateDietMutation,
  useAdminDeleteDietMutation,
  useAdminGetCentersQuery,
  useAdminCreateCenterMutation,
  useAdminUpdateCenterMutation,
  useAdminDeleteCenterMutation,
  useAdminGetSlotsQuery,
  useAdminUpsertSlotMutation,
  useAdminDeleteSlotMutation,
  useAdminListApplicationsQuery,
  useAdminGradeApplicationMutation,
  useAdminSetExamNoMutation,
  useAdminDeferApplicationMutation,
  useAdminChangeApplicationCenterMutation,
  useAdminGetApplicationDeferralsQuery,
  useAdminGetApplicationDeferTargetsQuery,

  // exam sessions
  useAdminGetExamSessionsQuery,
  useAdminGetExamSessionQuery,
  useAdminCreateExamSessionMutation,
  useAdminUpdateExamSessionMutation,
  useAdminDeleteExamSessionMutation,
  useAdminAttachDietToSessionMutation,
  useAdminDetachDietFromSessionMutation,

  // timetable entries
  useGetSessionTimetableQuery,
  useAdminGetSessionTimetableQuery,
  useAdminCreateTimetableEntryMutation,
  useAdminUpdateTimetableEntryMutation,
  useAdminDeleteTimetableEntryMutation,

  // exam courses
  useAdminGetCoursesByExamQuery,

  // admission cards
  useGetMyAdmissionCardQuery,
  useAdminGetAdmissionCardQuery,
  useAdminGetSessionAdmissionCardsQuery,

  // application course registration
  useAdminGetApplicationCoursesQuery,
  useAdminRegisterApplicationCourseMutation,
  useAdminUnregisterApplicationCourseMutation,
} = examApi;
